import { PromotionTier } from '@/services/settlement/settlementPromotion/types'

// BE 는 할인값을 컬럼 스케일 그대로 문자열로 내려준다 — promotion.discount_value DECIMAL(18,2) 라
// 정액 200 원이 "200.00", 정률 10% 가 "10.00" 으로 온다 (LOGISPRD-11361 에서 문자열로 전환).
// 화면·수정 프리필에 그 문자열을 그대로 쓰면 저장한 적 없는 소수부가 보인다 (LOGISPRD-11422).
//
// 숫자로 바꿔서 떼지 않는다 — 정액 상한이 정수부 16자리라 Number 로 바꾸는 순간 반올림된다 (LOGISPRD-11361).
// 타입은 string 이지만 옵셔널 체이닝으로 받는다 — 오류 행처럼 BE 가 값을 비워 보내는 경우가 있고,
// select 단계에서 던지면 셀 하나가 아니라 조회 전체가 죽는다 (PromotionBulkUploadModal.tsx:76 의 같은 사유)
export const stripTrailingZeros = (raw: string) => {
  if (!raw?.includes('.')) return raw
  const [whole, decimal] = raw.split('.')
  const kept = decimal.replace(/0+$/, '')
  return kept ? `${whole}.${kept}` : whole
}

// 할인값을 담은 응답 행의 정규화 (LOGISPRD-11772).
// 기간 프로모션은 할인값이 구간에 있으므로 행과 구간을 같이 뗀다 — 한쪽만 떼면
// 셀은 `10%` 인데 호버 툴팁만 `10.00%` 로 보인다.
// 프로모션 목록 응답과 정산 집계 응답이 같은 모양이라 한 함수를 쓴다.
export const normalizeDiscountScale = <T extends { discountValue: string; tiers?: { discountValue: string }[] }>(row: T): T => ({
  ...row,
  discountValue: stripTrailingZeros(row.discountValue),
  tiers: row.tiers?.map((tier) => ({ ...tier, discountValue: stripTrailingZeros(tier.discountValue) })),
})

// 정액은 화면에만 천 단위 쉼표를 붙인다 (폼에 담기는 값은 숫자 문자열 그대로).
// 정수부만 포맷하고 소수부는 원문을 둔다 — 숫자로 바꿔 포맷하면 16자리 값이 반올림된다 (LOGISPRD-11361).
// 새 입력에 소수부는 없지만(LOGISPRD-11314), 소수로 저장된 기존 건은 그대로 보여 준다
export const toDiscountDisplay = (raw: string, isRate: boolean) => {
  if (isRate || !raw) return raw
  // 문자열로 오기 전 응답(구 BE)이 섞여도 죽지 않게 받아 둔다 — 그 경우는 이미 반올림된 값이다
  const [whole, ...decimals] = String(raw).split('.')
  // toLocaleString 은 숫자를 받으므로 여기서도 못 쓴다 — 자릿수 그대로 세 자리마다 쉼표를 넣는다
  const head = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decimals.length ? `${head}.${decimals.join('')}` : head
}

// LOGISPRD-11390 — 목록 '할인값' 셀 툴팁에 펼칠 기간 프로모션 구간.
// 셀에는 `구간 N개` 만 들어가고, 진행중 건은 수정 모달이 잠겨 있어 이 툴팁이 구간을 확인할 유일한 통로다.
// 어휘는 등록 모달의 구간 표를 따른다 (구간 번호 · 마지막 구간은 '이상').
// 수량은 정수라 toLocaleString 으로 쉼표를 붙이고, 할인값은 문자열 그대로 다룬다 (LOGISPRD-11361)
export const toTierLines = (tiers: PromotionTier[], isFixed: boolean) =>
  tiers.map((tier, index) => ({
    label: `${index + 1}구간`,
    range:
      tier.toValue === null ? `${tier.fromValue.toLocaleString()} 이상` : `${tier.fromValue.toLocaleString()} ~ ${tier.toValue.toLocaleString()}`,
    discount: isFixed ? `${toDiscountDisplay(tier.discountValue, false)}원` : `${tier.discountValue}%`,
  }))

// LOGISPRD-11772 — 기간 프로모션의 할인값 열 표기. 프로모션 관리 목록과 파트너정산 프로모션 탭이
// 같은 프로모션을 다르게 적지 않도록 문구·구분자를 여기 한 곳에 둔다.
export const toTierSummaryText = (tiers?: PromotionTier[]) => (tiers?.length ? `구간 ${tiers.length}개` : '-')

// 셀에는 개수만 들어가므로 구간별 값은 호버로 편다. 줄바꿈은 받는 쪽 툴팁이 살린다
// (`GridTooltip` 의 white-space: pre-line · `PromotionTierTooltip` 의 3열 그리드)
export const toTierTooltipText = (tiers: PromotionTier[], isFixed: boolean) =>
  toTierLines(tiers, isFixed)
    .map((line) => `${line.label} ${line.range} ${line.discount}`)
    .join('\n')
